import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';

interface Language {
  code: string;
  name: string; // Native name (endonym) - language name in its own language
  flag: string;
}

// Language names in their native languages (endonyms)
const languages: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },      // English in English
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },      // German in German
  { code: 'es', name: 'Español', flag: '🇪🇸' },      // Spanish in Spanish
  { code: 'fr', name: 'Français', flag: '🇫🇷' },     // French in French
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },        // Finnish in Finnish
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },       // Turkish in Turkish
];

interface LanguageSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LanguageSelectorModal: React.FC<LanguageSelectorModalProps> = ({ isOpen, onClose }) => {
  const { i18n, t } = useTranslation('common');
  const { refreshPreferences } = useUserPreferences();

  if (!isOpen) return null;

  // Get current language, handling language codes like 'tr-TR' -> 'tr'
  const currentLangCode = i18n.language.split('-')[0].toLowerCase();

  const handleLanguageChange = async (langCode: string) => {
    // CRITICAL: Change language without page reload or navigation
    // This preserves ALL state:
    // - React component state
    // - Form data (unsaved changes)
    // - Current page/route
    // - Scroll position
    // - Modal states
    // - Any unfinished operations
    
    // i18n.changeLanguage() is async but does NOT cause:
    // - Page reloads
    // - Navigation
    // - State loss
    // - Component unmounting
    
    try {
      // Cookie is set by i18n languageChanged handler when we changeLanguage below
      
      // Change the language (i18n will persist to cookie via languageChanged)
      await i18n.changeLanguage(langCode);

      // Persist language to user preferences (backend)
      try {
        await apiService.updateUserPreferences({ language: langCode });
        await refreshPreferences();
      } catch (e) {
        console.warn('Failed to save language preference:', e);
      }

      // Language changed successfully - all state preserved
      onClose();
    } catch (error) {
      console.error('Failed to change language:', error);
      // Even on error, don't navigate or reload
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('language.select')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Language List */}
        <div className="p-6">
          <div className="space-y-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center space-x-4 px-4 py-4 rounded-lg transition-all ${
                  currentLangCode === lang.code
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 dark:border-blue-400'
                    : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="text-3xl">{lang.flag}</span>
                <div className="flex-1 text-left">
                  <div className={`font-medium ${
                    currentLangCode === lang.code
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}>
                    {lang.name}
                  </div>
                  {currentLangCode === lang.code && (
                    <div className="text-sm text-blue-500 dark:text-blue-400 mt-1">
                      {t('language.current')}
                    </div>
                  )}
                </div>
                {currentLangCode === lang.code && (
                  <div className="w-6 h-6 rounded-full bg-blue-500 dark:bg-blue-400 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelectorModal;
